<template>
  <Dialog
    v-model="useDialogStore().addons.isOpen"
    size="medium"
    @open="onOpen"
    @close="onClose"
  >
    <template #header="{ close }">
      <DialogHeader
        :title="useT('addon.list.title')"
        :show-close="true"
        @close="close"
      />
    </template>

    <div
      v-if="isLoading"
      v-loading="true"
      class="h-80 w-full"
    />
    <div v-else>
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-2xl font-semibold">
          {{ useT('addon.list.title') }}
        </h2>

        <Button
          type="primary"
          icon-left="plus"
          @click="useDialogStore().open('addonCreate', {
            workspaceDomain: useDialogStore().addons.workspaceDomain,
            onSubmit: () => refresh(),
          })"
        >
          {{ useT('addon.create.title') }}
        </Button>
      </div>

      <!-- Master AI switch — independent of addon credit balance. Sits at
           the same level as the addons list per UX request. -->
      <div class="flex items-center justify-between rounded-lg border border-gray-200 p-3 mb-4">
        <div>
          <div class="text-sm font-medium">
            {{ useT('addon.aiEnabled') }}
          </div>
          <div class="text-xs text-gray-500">
            {{ useT('addon.aiEnabled.description') }}
          </div>
        </div>
        <Switch
          :model-value="aiEnabled"
          :disabled="isSaving"
          @update:model-value="onToggleAi"
        />
      </div>

      <!-- Per-user AI access (ai.enabledTo). Effective while the master
           switch is off — with it on, everyone in the workspace has AI and
           the list has no effect. -->
      <div class="rounded-lg border border-gray-200 p-3 mb-4">
        <div class="text-sm font-medium">
          {{ useT('addon.enabledTo') }}
        </div>
        <div class="text-xs text-gray-500 mb-2">
          {{ useT('addon.enabledTo.description') }}
        </div>
        <Select
          :model-value="enabledTo"
          :options="userOptions"
          :placeholder="useT('addon.enabledTo.placeholder')"
          :disabled="isSaving"
          multiple
          filterable
          collapse-tags
          class="w-full"
          @update:model-value="onUpdateEnabledTo"
        />
      </div>

      <!-- MCP opt-in (ai.mcpEnabled). The interface gates the hosted MCP
           server on this AND the AI gate above it, so with AI shut this
           switch writes a field nothing acts on — hence the warning, and the
           refusal to turn it ON from that state. Turning it back OFF always
           stays available, or a workspace could get stuck opted in.

           All three controls also take turns while any one of them is saving.
           The gate is decided from store state, so a click landing mid-save
           would be judged against a gate that is already gone — and worse, two
           responses in flight can arrive out of order and merge a stale
           `mcpEnabled` over a newer one. -->
      <div class="rounded-lg border border-gray-200 p-3 mb-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="text-sm font-medium">
              {{ useT('addon.mcpEnabled') }}
            </div>
            <div class="text-xs text-gray-500">
              {{ useT('addon.mcpEnabled.description') }}
            </div>
          </div>
          <Switch
            :model-value="mcpEnabled"
            :disabled="isMcpSwitchLocked({ saving: isSaving, aiGateOpen, mcpEnabled })"
            @update:model-value="onToggleMcp"
          />
        </div>
        <div
          v-if="!aiGateOpen"
          class="text-xs text-orange-700 mt-2"
        >
          {{ useT('addon.mcpEnabled.aiRequired') }}
        </div>
      </div>

      <!-- Granted credit sets. Sits above the add-ons list because an add-on
           says what was promised and a credit set says what the workspace can
           actually spend, and only the second one answers "why does it still
           show 47". -->
      <AddonCreditSets :workspace-domain="useDialogStore().addons.workspaceDomain" />

      <div
        v-if="!useAddonsStore().addons.length"
        class="text-sm text-gray-500 text-center py-8"
      >
        {{ useT('addon.list.empty') }}
      </div>
      <div
        v-else
        class="space-y-3"
      >
        <AddonItem
          v-for="addon in useAddonsStore().addons"
          :key="addon._id"
          :addon="addon"
          @click="useDialogStore().open('addonEdit', {
            id: addon._id,
            workspaceDomain: useDialogStore().addons.workspaceDomain,
            onSubmit: () => refresh(),
          })"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup>
const isLoading = ref(false);
const isAiToggling = ref(false);
const isMcpToggling = ref(false);

const aiEnabled = computed(() => Boolean(useAddonsStore().ai?.enabled));
const mcpEnabled = computed(() => Boolean(useAddonsStore().ai?.mcpEnabled));

// Whether AI can be reached by anyone here — the master switch or a non-empty
// per-user list. MCP needs this open on top of its own flag, so it decides
// whether the MCP switch is offerable rather than merely decorative.
const aiGateOpen = computed(() => isAiGateOpen(useAddonsStore().ai));

// The three controls take turns. They all write the same `ai` block, and closing
// the AI gate withdraws the MCP opt-in as a side effect, so two in flight at
// once means the older response can merge a stale value over the newer one and
// leave the dialog disagreeing with the database.
const isSaving = computed(() => isAiToggling.value || isEnabledToSaving.value || isMcpToggling.value);

const workspaceUsers = ref([]);
const isEnabledToSaving = ref(false);

const userOptions = computed(() => workspaceUsers.value.map((user) => {
  const label = user.fullName ? `${user.fullName} (${user.email})` : user.email;

  return {
    id: String(user._id),
    text: user.isSupport ? `${label} ${useT('addon.enabledTo.supportTag')}` : label,
  };
}));

const enabledTo = computed(() => (useAddonsStore().ai?.enabledTo || []).map((userId) => String(userId)));

const onUpdateEnabledTo = async(value) => {
  isEnabledToSaving.value = true;
  const response = await useAddonsStore().setAiEnabledTo({
    workspaceDomain: useDialogStore().addons.workspaceDomain,
    enabledTo: value,
  });
  isEnabledToSaving.value = false;
  if (response) {
    // `mcpEnabled` comes back too, because emptying this list can close the AI
    // gate and the server withdraws the MCP opt-in in the same write. Merging
    // only `enabledTo` would leave the MCP switch drawn ON next to the warning
    // saying MCP cannot work, and send the operator away believing it is on.
    useAddonsStore().ai = {
      ...useAddonsStore().ai,
      enabledTo: response.enabledTo,
      mcpEnabled: Boolean(response.mcpEnabled),
    };
  }
};

const refresh = async() => {
  await useAddonsStore().fetchAddons({
    workspaceDomain: useDialogStore().addons.workspaceDomain,
  });
};

const onToggleAi = async(value) => {
  isAiToggling.value = true;
  const response = await useAddonsStore().setAiEnabled({
    workspaceDomain: useDialogStore().addons.workspaceDomain,
    enabled: value,
  });
  isAiToggling.value = false;
  if (response) {
    // `mcpEnabled` rides along: switching AI off closes the gate, and the server
    // withdraws the MCP opt-in in the same write. Merging only `enabled` would
    // keep a stale `true` here and draw the MCP switch ON beside the warning
    // that says MCP cannot work.
    useAddonsStore().ai = {
      ...useAddonsStore().ai,
      enabled: Boolean(response.enabled),
      mcpEnabled: Boolean(response.mcpEnabled),
    };
  }
};

const onToggleMcp = async(value) => {
  isMcpToggling.value = true;
  const response = await useAddonsStore().setMcpEnabled({
    workspaceDomain: useDialogStore().addons.workspaceDomain,
    enabled: value,
  });
  isMcpToggling.value = false;
  if (response) {
    // Optimistically reflect the new state without waiting for a re-fetch.
    useAddonsStore().ai = { ...useAddonsStore().ai, mcpEnabled: Boolean(response.mcpEnabled) };
  }
};

const onOpen = async() => {
  isLoading.value = true;
  const [, users] = await Promise.all([
    refresh(),
    useWorkspacesStore().fetchUsers(useDialogStore().addons.workspaceDomain),
  ]);
  workspaceUsers.value = users;
  isLoading.value = false;

  useRouter().push({
    query: {
      workspace: useDialogStore().addons.workspaceDomain,
      showAddons: true,
    },
  });
};

const onClose = () => {
  useRouter().push({
    query: {
      ...useRoute().query,
      showAddons: undefined,
    },
  });
};

onMounted(() => {
  if (useRoute()?.query?.showAddons) {
    useDialogStore().open('addons', {
      workspaceDomain: useRoute().query?.workspace,
    });
  }
});
</script>
